import BaseModel from './BaseModel';
import * as AT from '../../components/container/attributes';
import Contact from './Contact';
import Project from './Project';
import Staff from './Staff';
import {HitOnlineEnums} from 'hit-online-web-ui/src/helpers/constants';
import {translate} from '../../plugins/i18n/i18n';

export default class NssoHistory extends BaseModel {
  constructor({
    id,
    number,
    designation,
    project,
    staff,
    contact,
    creationDate = undefined,
    operationDate = undefined,
    registrationNo = undefined,
    errorNo = undefined,
    errorMessage = undefined,
    operationStatus = undefined,
    operationType = undefined,
    source = undefined,
  }) {
    super(id, number, designation);
    this.project = project ? new Project(project) : undefined;
    this.staff = staff ? new Staff(staff) : undefined;
    this.contact = contact ? new Contact(contact) : undefined;
    this.creationDate = creationDate;
    this.operationDate = operationDate;
    this.registrationNo = registrationNo;
    this.errorNo = errorNo;
    this.errorMessage = errorMessage;
    this.operationStatus = operationStatus
      ? HitOnlineEnums.getEnumValue(
          'HIT_OPERATION_STATUS',
          operationStatus,
          translate,
        )
      : null;
    this.operationType = operationType
      ? HitOnlineEnums.getEnumValue(
          'HIT_OPERATION_TYPE',
          operationType,
          translate,
        )
      : null;
    this.source = source
      ? HitOnlineEnums.getEnumValue(
          'HIT_NSSO_HISTORY_SOURCE',
          source,
          translate,
        )
      : null;
  }

  static get baseModuleForRedirection() {
    return 'check-in-at-work';
  }
  get baseModuleForRedirection() {
    return NssoHistory.baseModuleForRedirection;
  }

  static get printTemplateType() {
    return 'nssoHistory';
  }
  get printTemplateType() {
    return NssoHistory.printTemplateType;
  }

  static get fkName() {
    return 'nsso_history_id';
  }
  static get apiRoute() {
    return 'nsso_history';
  }
  static get detailPageRoute() {
    return 'nssoHistoryDetail';
  }
  static get listPageRoute() {
    return 'nssoHistoryList';
  }
  static get joinFilterColumns() {
    return ['search'];
  }
  get fkName() {
    return NssoHistory.fkName;
  }
  get apiRoute() {
    return NssoHistory.apiRoute;
  }
  get listPageRoute() {
    return NssoHistory.listPageRoute;
  }
  get detailPageRoute() {
    return NssoHistory.detailPageRoute;
  }
  get joinFilterColumns() {
    return NssoHistory.joinFilterColumns;
  }

  static get entityListDisplayAttrs() {
    return this.getAttributes([
      'number',
      'designation',
      'project',
      'staff',
      'contact',
      'creationDate',
      'operationDate',
      'operationType',
      'operationStatus',
      'registrationNo',
      'errorNo',
      'errorMessage',
      'source',
    ]);
  }

  static get joinAttrsKey() {
    return ['number', 'designation', 'project', 'staff', 'contact'];
  }

  static get browserDisplayAttrs() {
    return this.getAttributes(['project', 'staff', 'number', 'designation']);
  }

  static get positionInformationAttrs() {
    return this.getAttributes([], ['project', 'number', 'designation']);
  }

  static get staffAttributes() {
    return this.getAttributes(['operationDate', 'project', 'operationStatus']);
  }
  static get projectAttributes() {
    return this.getAttributes(
      ['operationDate', 'person', 'operationStatus'],
      ['staff', 'contact'],
    );
  }
  static get nssoHistoryAttributes() {
    return this.getAttributes(
      ['person', 'operationStatus', 'operationType', 'operationDate'],
      ['staff', 'contact', 'creationUser'],
    );
  }

  static get allAttributes() {
    return [
      new AT.FkProjectAttribute({
        name: 'project',
        joinPath: ['project!fk_nsso_history_project_id_project'],
        column: 'project_id',
        fkToUpdate: 'project_id',
        tableProperties: {
          header: 'hit-base.common.project',
        },
      }),
      new AT.NotFetchedAttribute({
        name: 'person',
        tableProperties: {
          header: 'hit-app.common.worker',
        },
        transform: (data) => {
          if (data?.staff) return data?.staff?.name;
          return `${data?.contact?.firstName} ${data?.contact?.lastName}`;
        },
      }),
      new AT.FkStaffAttribute({
        name: 'staff',
        column: 'staff_id',
        joinPath: ['staff!fk_nsso_history_staff_id_staff'],
      }),
      new AT.FkContactAttribute({
        name: 'contact',
        joinPath: ['contact!fk_nsso_history_contact_id_contact'],
        column: 'contact_id',
      }),
      new AT.DateAttribute({
        name: 'creationDate',
        column: 'creation_date',
        tableProperties: {
          header: 'hit-base.common.creation-date',
        },
      }),
      new AT.FkStaffAttribute({
        name: 'creationUser',
        joinPath: ['staff!fk_nsso_history_creation_user_id_staff'],
        column: 'creation_user_id',
      }),
      new AT.DateTimeSecondsAttribute({
        name: 'operationDate',
        column: 'operation_date',
        tableProperties: {
          header: 'hit-app.common.date',
        },
      }),
      new AT.StringAttribute({
        name: 'registrationNo',
        column: 'registration_no',
        tableProperties: {
          header: 'hit-app.nsso.registration-no',
          maxWidth: '1fr',
        },
      }),
      new AT.StringAttribute({
        name: 'errorNo',
        column: 'error_no',
      }),
      new AT.StringAttribute({
        name: 'errorMessage',
        column: 'error_message',
      }),
      new AT.EnumerationAttribute({
        name: 'operationType',
        enumType: 'HIT_OPERATION_TYPE',
        enumClass: HitOnlineEnums,
        column: 'operation_type',
        tableProperties: {
          header: 'hit-app.nsso.operation-type',
        },
      }),
      new AT.EnumerationAttribute({
        name: 'operationStatus',
        enumType: 'HIT_OPERATION_STATUS',
        enumClass: HitOnlineEnums,
        column: 'operation_status',
        tableProperties: {
          header: 'hit-app.nsso.status',
          maxWidth: '0.5fr',
        },
      }),
      new AT.EnumerationAttribute({
        name: 'source',
        enumType: 'HIT_NSSO_HISTORY_SOURCE',
        enumClass: HitOnlineEnums,
        column: 'source',
        tableProperties: {
          header: 'hit-app.nsso.source',
        },
      }),
      new AT.StringAttribute({
        name: 'parentId',
        column: 'parent_id',
      }),
    ];
  }
}
